import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { CaretDown } from "components/icons/CaretDown";
import { CaretRight } from "components/icons/CaretRight";
import { useMemo } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { Invoice, InvoiceItem } from "types/Models/invoice";
import { ArrayElement } from "types/utils/ArrayElement";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";

export const ItemScheduleDetails = ({
  invoiceItem,
}: {
  invoiceItem: Invoice["invoice_items"][number];
}) => {
  const columnHelper =
    createColumnHelper<
      ArrayElement<NonNullable<InvoiceItem["revenue_schedule"]>>
    >();

  const columns = [
    columnHelper.accessor("period", {
      header: "Period",
      cell: (info) => info.getValue(),
      size: 25,
    }),

    columnHelper.accessor("amount", {
      header: () => <div className="t-text-right">Recognized Amount</div>,
      cell: (info) => (
        <div className="t-text-right">
          <AmountSuperScript amount={Number(info.getValue())} />{" "}
        </div>
      ),
      size: 45,
    }),

    columnHelper.accessor("status", {
      header: () => <div className="t-text-right">Status</div>,
      cell: (info) => (
        <div className="t-text-right">
          <Tag
            transparent
            tagType={info.getValue() === "RECOGNIZED" ? "green" : "yellow"}
          >
            {capitalizeFirstLetter(info.getValue().toLowerCase())}
          </Tag>
        </div>
      ),
      size: 30,
    }),
  ];

  const schedule = invoiceItem.revenue_schedule || [];

  const itemTotal =
    Math.round(schedule.reduce((sum, s) => sum + Number(s.amount), 0) * 100) /
    100;

  const data = useMemo(() => schedule, [schedule]);

  const table = useReactTable({
    data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalRow = (
    <Table.Row>
      <Table.Cell style={{ width: "25%" }} className="t-text-text-30">
        Total Amount
      </Table.Cell>
      <Table.Cell
        style={{ width: "45%" }}
        className="t-text-right t-text-text-100 t-font-medium"
      >
        <AmountSuperScript amount={itemTotal} />
      </Table.Cell>
      <Table.Cell style={{ width: "30%" }} />
    </Table.Row>
  );

  return (
    <TableUI sticky={false} table={table} size="regular" moreRows={totalRow} />
  );
};

export const AllItemScheduleDetails = ({ invoice }: { invoice: Invoice }) => {
  const allSchedules = invoice.invoice_items.flatMap(
    (item) => item.revenue_schedule || []
  );

  const totalRecognized =
    Math.round(
      allSchedules
        .filter((s) => s.status === "RECOGNIZED")
        .reduce((sum, s) => sum + Number(s.amount), 0) * 100
    ) / 100;

  const totalDeferred =
    Math.round(
      allSchedules
        .filter((s) => s.status === "DEFERRED")
        .reduce((sum, s) => sum + Number(s.amount), 0) * 100
    ) / 100;

  return (
    <Accordion.Item value="ITEM_SCHEDULE_DETAILS">
      <Accordion.Trigger disabled>
        <span>Revenue Recognition</span>
        <div className="t-flex t-gap-3 t-font-normal">
          <span className="t-flex t-gap-1.5 t-items-center">
            <Tag transparent tagType="green">
              Recognized
            </Tag>
            <span className="t-text-text-100 t-font-medium t-text-body-sm">
              <AmountSuperScript amount={totalRecognized} />
            </span>
          </span>
          <span className="t-flex t-gap-1.5 t-items-center">
            <Tag transparent tagType="yellow">
              Deferred
            </Tag>
            <span className="t-text-text-100 t-font-medium t-text-body-sm">
              <AmountSuperScript amount={totalDeferred} />
            </span>
          </span>
        </div>
      </Accordion.Trigger>
      <Accordion.Content>
        <Accordion.Root
          defaultValue={["item-0"]}
          type="multiple"
          className="t-flex t-flex-col t-gap-3"
        >
          {invoice.invoice_items.map((item, index) => (
            <Accordion.RawItem value={`item-${index}`} key={item.uuid}>
              <Accordion.RawTrigger asChild>
                <button className="all:unset t-flex t-gap-2 t-items-center t-text-text-100 t-group t-py-2 t-w-full hover:t-bg-surface-lighter-grey focus-visible:t-outline focus-visible:t-outline-offset-1 t-outline-purple-30">
                  <CaretRight
                    strokeWidth="1.4"
                    className="group-data-state-open:t-rotate-90 t-transform t-transition t-duration-300 t-ease-in-out"
                  />
                  <div className="t-text-body-sm t-leading-5 t-font-medium">
                    {item.description}
                  </div>
                </button>
              </Accordion.RawTrigger>
              <Accordion.RawContent className="t-overflow-hidden data-state-open:t-animate-accordian-slide-down data-state-closed:t-animate-accordian-slide-up t-mt-1">
                <ItemScheduleDetails invoiceItem={item} />
              </Accordion.RawContent>
            </Accordion.RawItem>
          ))}
        </Accordion.Root>
      </Accordion.Content>
    </Accordion.Item>
  );
};
